import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, AlertTriangle, Mail, MessageCircle, Phone } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import {
  CONTACT_EMAIL,
  CONTACT_EMAIL_HREF,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_HREF,
  CONTACT_WHATSAPP_DISPLAY,
  getWhatsAppUrl,
} from "@/lib/contact";

export const Footer = () => {
  const { t } = useTranslation();
  const whatsappHref = getWhatsAppUrl(t("common.whatsappMessage"));
  const categories = [
    { key: "hormonalHealth", base: "/hormonal-health" },
    { key: "fertilityPregnancy", base: "/fertility-pregnancy" },
    { key: "menopauseAging", base: "/menopause-aging" },
    { key: "preventiveMedicine", base: "/preventive-medicine" },
  ].map((c) => ({
    ...c,
    label: t(`nav.${c.key}`),
    items: t(`${c.key}.items`, { returnObjects: true }) || [],
  }));
  const legal = t("legal.pages", { returnObjects: true }) || [];
  const year = new Date().getFullYear();

  const col = (title, links) => (
    <div>
      <h4 className="mb-4 text-sm font-semibold uppercase tracking-editorial text-warm-ivory/60">
        {title}
      </h4>
      <ul className="space-y-2.5">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="text-sm text-warm-ivory/80 transition-colors hover:text-champagne-gold"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-deep-ink text-warm-ivory">
      <div className="grain absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div className="max-w-sm">
            <Logo tone="ivory" />
            <p className="mt-6 text-sm leading-relaxed text-warm-ivory/70">
              {t("footer.description")}
            </p>
            <div className="mt-6 flex items-start gap-3 text-sm text-warm-ivory/70">
              <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-champagne-gold" />
              <span>{t("patientService.contact.address")}</span>
            </div>
            <div className="mt-4 space-y-2 text-sm text-warm-ivory/70">
              <a href={CONTACT_PHONE_HREF} className="flex items-center gap-3 transition-colors hover:text-champagne-gold">
                <Phone className="h-4 w-4 flex-shrink-0 text-champagne-gold" />
                <span>{CONTACT_PHONE_DISPLAY}</span>
              </a>
              <a href={CONTACT_EMAIL_HREF} className="flex items-center gap-3 transition-colors hover:text-champagne-gold">
                <Mail className="h-4 w-4 flex-shrink-0 text-champagne-gold" />
                <span>{CONTACT_EMAIL}</span>
              </a>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 transition-colors hover:text-champagne-gold"
              >
                <MessageCircle className="h-4 w-4 flex-shrink-0 text-champagne-gold" />
                <span>{t("patientService.contact.whatsappTitle")}: {CONTACT_WHATSAPP_DISPLAY}</span>
              </a>
            </div>
          </div>

          {col(t("footer.exploreTitle"), [
            { to: "/about", label: t("nav.about") },
            { to: "/philosophy", label: t("nav.philosophy") },
            ...categories.map((c) => ({ to: c.base, label: c.label })),
          ])}

          {col(t("footer.careTitle"), [
            ...categories.flatMap((c) => c.items.slice(0, 1).map((it) => ({ to: `${c.base}/${it.slug}`, label: it.title }))),
          ])}

          {col(t("footer.serviceTitle"), [
            { to: "/approach", label: t("nav.approach") },
            { to: "/patient-service", label: t("nav.patientService") },
            { to: "/contact", label: t("patientService.contact.title") },
            { to: "/faq", label: t("nav.faq") },
            { to: "/portal", label: t("nav.portal") },
          ])}
        </div>

        <div className="mt-14 flex items-start gap-3 rounded-2xl border border-warm-ivory/15 bg-warm-ivory/5 p-5 text-sm text-warm-ivory/70">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-terracotta" />
          <span>{t("footer.emergencyNote")}</span>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-warm-ivory/15 pt-8 text-sm text-warm-ivory/60 md:flex-row md:items-center md:justify-between">
          <p>{t("footer.rights", { year })}</p>
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            {legal.map((l) => (
              <li key={l.slug}>
                <Link to={`/legal/${l.slug}`} className="transition-colors hover:text-champagne-gold">
                  {l.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
};
